import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useEffect, useState } from 'react';

import { NoAccess } from '../NoAccess';

import { ApiError, usePlatformApi } from './api';
import { PlatformRoutes } from './routes';

/**
 * Outcome of the `GET /platform/access` probe.
 * - `loading`  — probe in flight.
 * - `granted`  — 200: the caller is a platform admin; show the console.
 * - `denied`   — any non-200 (403 / 401 / network / 5xx): fail closed to the
 *                NoAccess screen. This is a console-only SITE — there is no
 *                tenant-app fallback here.
 */
export type PlatformGateStatus = 'loading' | 'granted' | 'denied';

/**
 * Pure gate decision, extracted so it is unit-testable without a DOM/React
 * harness. The platform console is visible only on an explicit `granted`.
 * `loading` and `denied` both keep it hidden (fail-closed).
 */
export function isConsoleVisible(status: PlatformGateStatus): boolean {
  return status === 'granted';
}

const useStyles = makeStyles({
  probe: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

/**
 * SITE gate for the platform-admin console. Probes platform access on mount and
 * either renders the console ({@link PlatformRoutes} on a 200) or the
 * {@link NoAccess} screen (any non-200). A brief spinner covers the probe.
 *
 * Fail-closed: a 403 is the expected "not a platform admin" answer, but ANY
 * other failure (network error, 401 re-auth, 5xx) also denies the console — a
 * non-200 can only ever mean "no platform access", so denying is the safe
 * default. This site never falls back to the tenant application shell; that is a
 * separate deployed site.
 */
export function PlatformGate(): JSX.Element {
  const styles = useStyles();
  const api = usePlatformApi();
  const [status, setStatus] = useState<PlatformGateStatus>('loading');
  const [detail, setDetail] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    async function probe(): Promise<void> {
      try {
        await api.getAccess();
        if (!cancelled) {
          setStatus('granted');
        }
      } catch (error) {
        // 403 is the expected "not a platform admin" answer; any other failure
        // (network, 401 re-auth redirect, 5xx) also denies the console.
        const info =
          error instanceof ApiError
            ? `access probe returned HTTP ${error.status}`
            : `access probe failed: ${error instanceof Error ? error.message : String(error)}`;
        // eslint-disable-next-line no-console
        console.warn(`[platform] ${info}; showing no-access screen.`);
        if (!cancelled) {
          setDetail(info);
          setStatus('denied');
        }
      }
    }

    void probe();
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (status === 'loading') {
    return (
      <div className={styles.probe}>
        <Spinner label="Loading WorkLogix Platform…" />
      </div>
    );
  }

  if (isConsoleVisible(status)) {
    return <PlatformRoutes />;
  }

  // Denied: fail closed to a clear, full-screen no-access state (never blank,
  // never the tenant shell). The probe detail explains why (e.g. HTTP 401 vs 403).
  return <NoAccess detail={detail} />;
}
